import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Smartphone, X, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MobileMoneyDialog — payment flow for PawaPay (Airtel, Orange, M-Pesa, MTN).
 *
 * Props:
 *   open       — boolean, controls visibility
 *   onClose    — () => void, called when user closes the modal
 *   onSuccess  — () => void, called after payment completes (parent decides
 *                whether to refresh membership, close overlays, etc.)
 *   purpose    — "subscription" | "item"
 *   plan       — subscription plan code ("1m"/"3m"/"6m"/"12m"), required when
 *                purpose === "subscription"
 *   itemId     — item UUID, required when purpose === "item"
 *   itemTitle  — optional label to show above the amount
 *
 * The modal walks through 3 stages:
 *   1. `form`       — pick provider + enter phone
 *   2. `pending`    — payment initiated, waiting for the user to confirm on
 *                     their phone; polls status every 3s
 *   3. `success` / `failed` — final state, with a close/retry button
 */
const MobileMoneyDialog = ({
  open,
  onClose,
  onSuccess,
  purpose,
  plan,
  itemId,
  itemTitle,
}) => {
  const [providers, setProviders] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [provider, setProvider] = useState("");
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("form"); // form | pending | success | failed
  const [depositId, setDepositId] = useState(null);
  const [depositInfo, setDepositInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const pollTimerRef = useRef(null);

  // Load providers when dialog opens
  useEffect(() => {
    if (!open) return;
    setStage("form");
    setDepositId(null);
    setDepositInfo(null);
    setErrorMsg("");
    axios
      .get(`${API}/mobile-money/providers`)
      .then((r) => {
        setProviders(r.data.providers || []);
        setConfigured(r.data.configured);
        // Default to Airtel DRC if present
        const drcAirtel = (r.data.providers || []).find((p) => p.code === "AIRTEL_OAPI");
        if (drcAirtel) setProvider(drcAirtel.code);
        else if (r.data.providers?.length) setProvider(r.data.providers[0].code);
      })
      .catch(() => {
        toast.error("Could not load mobile-money providers");
        setConfigured(false);
      });
  }, [open]);

  // Stop polling when dialog closes
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const startPolling = (id) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    let ticks = 0;
    pollTimerRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/mobile-money/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const status = resp.data?.status;
        setDepositInfo(resp.data);
        if (status === "completed") {
          clearInterval(pollTimerRef.current);
          setStage("success");
          toast.success("Payment received — access unlocked!", { duration: 5000 });
          if (onSuccess) onSuccess();
        } else if (status === "failed" || status === "expired" || status === "failed_initiate") {
          clearInterval(pollTimerRef.current);
          setStage("failed");
          setErrorMsg(resp.data?.error || "The payment did not go through.");
        }
      } catch (err) {
        // Keep polling on transient errors
        console.warn("status poll error", err);
      }
      // Stop polling after ~5 minutes (100 * 3s = 300s)
      if (ticks > 100) {
        clearInterval(pollTimerRef.current);
        setStage("failed");
        setErrorMsg("Payment timed out. Please try again.");
      }
    }, 3000);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!provider) return toast.error("Please pick a provider");
    if (!phone || phone.replace(/\D/g, "").length < 9) return toast.error("Please enter a valid phone number");

    setSubmitting(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/mobile-money/init`,
        { purpose, plan, item_id: itemId, provider, phone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepositId(resp.data.deposit_id);
      setDepositInfo(resp.data);
      setStage("pending");
      startPolling(resp.data.deposit_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Could not initiate payment";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
      onClick={close}
      data-testid="mobile-money-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-slate-900">Pay with Mobile Money</h2>
          </div>
          <button onClick={close} className="text-slate-500 hover:text-slate-800" data-testid="mm-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!configured && (
          <div className="px-6 py-6 text-sm text-slate-700">
            Mobile Money is not configured on this server yet. Please use card payment for now.
            <div className="mt-4 text-right">
              <button
                className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm"
                onClick={close}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {configured && stage === "form" && (
          <form onSubmit={submit} className="px-6 py-5 space-y-4" data-testid="mm-form">
            {itemTitle && (
              <div className="text-xs text-slate-500 uppercase tracking-widest">
                Paying for
                <div className="text-sm text-slate-800 normal-case tracking-normal mt-1 truncate">
                  {itemTitle}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
                Mobile Money provider
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                data-testid="mm-provider-select"
                required
              >
                <option value="">— Choose a provider —</option>
                {providers.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
                Phone number
              </label>
              <input
                type="tel"
                inputMode="tel"
                placeholder="e.g. 0812345678 or 243812345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                data-testid="mm-phone-input"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter the phone number linked to your mobile-money wallet. We&apos;ll send a payment prompt right after you click below.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="mm-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending prompt…</> : "Send payment prompt"}
            </button>

            <p className="text-[11px] text-slate-400 text-center pt-2">
              Powered by PawaPay. You&apos;ll be charged in the currency of your provider.
            </p>
          </form>
        )}

        {configured && stage === "pending" && (
          <div className="px-6 py-8 text-center" data-testid="mm-pending">
            <div className="w-14 h-14 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-4">
              <Smartphone className="w-7 h-7 text-orange-600 animate-pulse" />
            </div>
            <div className="text-slate-900 font-semibold mb-1">Check your phone</div>
            <div className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
              We just sent a payment prompt to <strong>{phone}</strong>. Approve the request on your phone to complete the payment.
            </div>
            {depositInfo?.amount_local && (
              <div className="mt-4 text-xs text-slate-500">
                Amount:{" "}
                <strong className="text-slate-800">
                  {depositInfo.amount_local.toLocaleString()} {depositInfo.currency}
                </strong>
              </div>
            )}
            <div className="mt-6">
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin mx-auto" />
              <div className="text-xs text-slate-400 mt-2">Waiting for confirmation…</div>
            </div>
            <button
              type="button"
              onClick={close}
              className="mt-6 text-xs text-slate-500 hover:text-slate-800 underline"
            >
              Cancel and close
            </button>
          </div>
        )}

        {configured && stage === "success" && (
          <div className="px-6 py-8 text-center" data-testid="mm-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-slate-900 font-semibold mb-1">Payment received</div>
            <div className="text-sm text-slate-600 max-w-xs mx-auto">
              Your access has been activated. Enjoy the archive!
            </div>
            <button
              onClick={close}
              className="mt-6 px-6 py-2.5 rounded-full bg-slate-800 text-white text-sm font-medium"
            >
              Done
            </button>
          </div>
        )}

        {configured && stage === "failed" && (
          <div className="px-6 py-8 text-center" data-testid="mm-failed">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <div className="text-slate-900 font-semibold mb-1">Payment did not go through</div>
            <div className="text-sm text-slate-600 max-w-xs mx-auto">
              {errorMsg || "Please try again or use a different payment method."}
            </div>
            <div className="mt-6 flex gap-3 justify-center">
              <button
                onClick={() => { setStage("form"); setErrorMsg(""); }}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-800 text-sm"
                data-testid="mm-retry"
              >
                Try again
              </button>
              <button
                onClick={close}
                className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMoneyDialog;
